// src/components/CsvUploader.js
import React, { useCallback, useState } from "react";
import Papa from "papaparse";
import "./CsvUploader.css";

export default function CsvUploader({ requiredHeaders }) {
  const [rows, setRows] = useState([]);
  const [duplicateSKUs, setDuplicateSKUs] = useState(new Set());
  const [missingProductIds, setMissingProductIds] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [filteredMissing, setFilteredMissing] = useState([]);
  const [copied, setCopied] = useState(false);

  // Parse CSV
  const parseFile = useCallback((file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const parsedRows = result.data;
        setRows(parsedRows);

        // Detect duplicates
        const seen = new Set();
        const dupes = new Set();
        parsedRows.forEach((row) => {
          if (seen.has(row.skuID)) {
            dupes.add(row.skuID);
          }
          seen.add(row.skuID);
        });
        setDuplicateSKUs(dupes);

        // Missing Product IDs
        const missing = parsedRows
          .filter((row) => !row.ProductId || row.ProductId.trim() === "")
          .map((row) => row.skuID);
        setMissingProductIds(missing);
      },
    });
  }, []);

  // Search SKU
  const searchSKU = () => {
    if (!searchText) {
      setFilteredData([]);
      setFilteredMissing([]);
      return;
    }

    const skuIds = searchText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);

    const result = rows
      .filter((r) => skuIds.includes(String(r.skuID)))
      .map((r) => ({ skuID: r.skuID, ProductIds: r.ProductId }));

    setFilteredData(result);

    // Missing in search
    const missing = result
      .filter((item) => !item.ProductIds || String(item.ProductIds).trim() === "")
      .map((item) => item.skuID);

    setFilteredMissing(missing);
  };

  // Copy IDs
  const copyProductIDs = () => {
    const allIds = filteredData
      .map((item) => item.ProductIds)
      .filter(Boolean)
      .join(", ");
    navigator.clipboard.writeText(allIds);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ flex: 1, padding: "1rem" }}>
      <h2>CSV Uploader & SKU Search</h2>

      <input
        type="file"
        accept=".csv"
        onChange={(e) => {
          if (e.target.files.length > 0) parseFile(e.target.files[0]);
        }}
      />

      {rows.length > 0 && (
        <>
          <h3>Search SKU IDs</h3>
          <textarea
            rows={4}
            style={{ width: "100%", marginBottom: "1rem" }}
            placeholder="Enter SKU IDs (one per line)"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <button onClick={searchSKU}>Search</button>
        </>
      )}

      {/* Search Results */}
      {filteredData.length > 0 && (
        <div className="csv-preview">
          <button onClick={copyProductIDs} style={{ marginBottom: "0.5rem" }}>
            Copy Product IDs
          </button>
          {copied && (
            <span style={{ color: "green", marginLeft: "10px" }}>✅ Copied!</span>
          )}

          {filteredMissing.length > 0 && (
            <div style={{ color: "red", marginBottom: "0.5rem" }}>
              Missing Product IDs in search: {filteredMissing.length}
              <details>
                <summary>Show SKUs</summary>
                <ul>
                  {filteredMissing.map((sku, i) => (
                    <li key={i}>{sku}</li>
                  ))}
                </ul>
              </details>
            </div>
          )}

          <table border="1" cellPadding="6" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>SKU ID</th>
                <th>Product IDs</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      color: duplicateSKUs.has(String(item.skuID))
                        ? "red"
                        : "inherit",
                    }}
                  >
                    {item.skuID}
                  </td>
                  <td
                    style={{
                      color:
                        !item.ProductIds || String(item.ProductIds).trim() === ""
                          ? "red"
                          : "inherit",
                    }}
                  >
                    {item.ProductIds || "(missing)"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Global Missing IDs */}
      {missingProductIds.length > 0 && (
        <div style={{ color: "red", marginTop: "1rem" }}>
          Missing Product IDs (overall file): {missingProductIds.length}
          <details>
            <summary>Show SKUs</summary>
            <ul>
              {missingProductIds.map((sku, i) => (
                <li key={i}>{sku}</li>
              ))}
            </ul>
          </details>
        </div>
      )}
    </div>
  );
}
